import {
    ApplicationPresenter,
    FlipIntent,
    HistoryManager,
    Logger,
    NOOP_PROMISE_VOID,
    Place,
    Scope,
    SingletonServices,
    type AlertSeverity,
} from 'cube'
import { Places } from '../RouteConsts'
import { MainKey } from './main.key'
import { AlertScope, HomeScope, MainScope, type DialogScope, type MenuItem } from './main.scope'

import AppStorage from '@/shared/AppStorage'

const LOG = Logger.get('MainPresenter')

const ANONYMOUS_TOKEN = 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9'
    + '.eyJpc3MiOiJsaWZlc2h1Yi5jb20uYnIiLCJpYXQiOjE3NjY3OTEwNTgsImV4cCI6MTc5ODMyNzA1OCwiYXVkIjoibGl'
    + 'mZXNodWIuY29tLmJyIiwic3ViIjoiYW5vbnltb3VzIiwiR2l2ZW5OYW1lIjoiU29tZW9uZSIsIlJvbGUiOiJVc2VyIn0'
    + '.rW2RF1DlXW0BOO961Y4DgFmwQsdcVlpkWtqYDmRfvmw'

export class MainPresenter extends ApplicationPresenter<MainScope> {
    // :: Instance

    private readonly homeScope = new HomeScope()
    private readonly menuManager = new MenuManager(this)
    private stopServices = NOOP_PROMISE_VOID

    public constructor(historyManager: HistoryManager) {
        super(historyManager, new MainScope())
        
        this.scope.header.update = this.update
        this.scope.menu.update = this.update

        this.homeScope.update = this.update

        this.setPlaces(Places)
    }

    initialize() {
        this.scope.onLogin = this.action(this.onLogin)
        this.scope.onHome = this.action(this.onHome.onClick)
        this.scope.onAnaliseBulas = this.action(this.onAnaliseBulas.onClick)
        this.scope.onAnaliseReacoes = this.action(this.onAnaliseReacoes.onClick)
        this.scope.update = this.update

        this.scope.header.version = '1.0.0'
        this.scope.header.title = 'Busca Certa I.A.'
        this.scope.header.subtitle = 'FAPESC / Lifeshub'
        this.scope.header.onHome = this.action(this.onHome.onClick)
        this.scope.header.onClickLogin = this.action(this.onLogin)
        this.scope.header.onAlert = this.action(this.onOpenAlert)
        this.scope.header.onIndicadores = this.action(this.onIndicadores)
        this.scope.header.onTrocaTema = this.action(this.onTrocaTema)
        this.scope.header.update = this.update

        this.scope.menu.version = 0
        this.scope.menu.items = []
        this.scope.menu.update = this.update

        this.homeScope.onOpenAlert = this.action(this.onOpenAlert)
        this.homeScope.update = this.update

        this.scope.page = this.homeScope

        return this.bootstrap()
    }

    public override release() {
        this.stopServices().catch(() => void 0)
        super.release()
    }

    private bootstrap() {
        let initialized = false

        const bootstrap = async () => {
            try {
                await this.kickStart(Places.main)
                initialized = true
            } catch (error) {
                LOG.error('Initializing', error)
                this.release()
            }
        }

        bootstrap().catch(LOG.caught)

        return () => {
            if (initialized) {
                this.release()
            }
        }
    }

    public override async applyParameters(
        intent: FlipIntent,
        initialization: boolean,
        last?: boolean
    ): Promise<boolean> {
        const keys = new MainKey(this, intent)

        if (initialization) {
            await this.intializeState(keys)

            try {
                const targetUri = this.newIntentFromString(this.historyManager.location)
                if (targetUri.toString() !== intent.toString()) {
                    await this.flipToIntent(targetUri)
                    return false
                }
            } catch (caught) {
                this.unexpected('Navigation from history', caught)
            }
        }

        if (last) {
            this.pageSlot(undefined)
        } else {
            keys.parentSlot = this.pageSlot
            keys.dialogSlot = this.dialogSlot
        }

        this.menuManager.ativateItemByPlace(intent.place)

        return true
    }

    private async intializeState(keys: MainKey) {
        this.stopServices = await SingletonServices.start()

        this.scope.header.loggedIn = true
        AppStorage.setAccessToken({
            token: ANONYMOUS_TOKEN
        }, false)

        this.buildMenu()

        this.scope.header.quickLinkData = [
            {
                label: this.onAnaliseBulas.label! as string,
                onClick: this.onAnaliseBulas.onClick!
            },
            {
                label: this.onAnaliseReacoes.label! as string,
                onClick: this.onAnaliseReacoes.onClick!
            }
        ]
        this.scope.header.quickLinkVersion++

        LOG.info(`Initialized(${keys.toString()})`)
    }

    // :: Helper API

    public override unexpected(message: string, error: unknown) {
        super.unexpected(message, error)
        this.alert('error', 'Unexpected error', message)
    }

    public override alert(severity: AlertSeverity, title: string, message: string, onClose?: () => Promise<void>) {
        const alertScope = new AlertScope()
        alertScope.severity = severity
        alertScope.title = title
        alertScope.message = message
        alertScope.minWidth = 300

        const saveClose = this.action(this.onCloseAlert)
        if (onClose) {
            alertScope.onClose = () => saveClose(onClose)
        } else {
            alertScope.onClose = saveClose
        }

        alertScope.primaryAction = {
            label: 'Fechar',
            action: alertScope.onClose
        }

        alertScope.update = this.update
        this.scope.alert = alertScope
    }

    // :: View Actions

    readonly pageSlot = (scope: Scope | undefined | null) => {
        this.scope.page = scope ?? this.homeScope
    }

    readonly dialogSlot = (scope: Scope | undefined | null) => {
        if (this.scope.dialog !== scope) {
            this.scope.dialog = scope as DialogScope

            if (this.scope.dialog && !this.scope.dialog.onClose) {
                LOG.error(`Missing onClose action on scope ${this.scope.dialog.constructor.name}`)
            }
        }
    }

    readonly onCloseAlert = async (onClose?: () => Promise<void>) => {
        this.scope.alert = undefined
        if (onClose) {
            await onClose()
        }
    }

    readonly onHome = this.menuManager.addItem(Places.main, {
        label: 'Página Inicial',
        icon: 'home',
        divider: true,
        onClick: async () => {
            this.scope.page = this.homeScope
            await this.flip(this.rootPlace)
        }
    })

    readonly onAnaliseBulas = this.menuManager.addItem(Places.analiseBulas, {
        label: 'Análise de Bulas',
        icon: 'medkit',
        onClick: async () => {
            await this.flip(Places.analiseBulas)
        }
    })

    readonly onAnaliseReacoes = this.menuManager.addItem(Places.analiseReacoes, {
        label: 'Análise de Reações',
        icon: 'hospital',
        onClick: async () => {
            await this.flip(Places.analiseReacoes)
        }
    })

    readonly onLogin = async () => {
        this.scope.header.loggedIn = true
        this.alert('info', 'Working in progress...', 'No implementation to this action, yet.')
    }

    readonly onIndicadores = async () => {
        this.alert('info', 'Informação', 'Implementar Indicadores', this.onCloseAlert)
    }

    readonly onTrocaTema = async () => {
        this.alert('success', 'Informação', 'Implementar troca de tema', this.onCloseAlert)
    }

    readonly onOpenAlert = async (severity: AlertSeverity) => {
        LOG.info('onAlert clicked')
        this.alert(severity, 'Some title', 'Some message', async () => {
            LOG.info(`Alert with severity ${severity} was closed`)
        })
    }

    protected buildMenu() {
        this.scope.menu.version++
        this.scope.menu.items = [
            this.onHome,
            this.onAnaliseBulas,
            // Removido neste momento - código mantido, por enquanto
            // this.onAnaliseReacoes
        ]
    }
}

class MenuManager {

    readonly owner: MainPresenter

    private activableMenuItenMap = new Map<Place, MenuItem>()

    constructor(owner: MainPresenter) {
        this.owner = owner
    }

    addItem(place: Place, item: MenuItem) {
        if (item.onClick) {
            this.activableMenuItenMap.set(place, item)

            const realOnClick = item.onClick
            item.onClick = this.owner.action(async () => {
                try {
                    await realOnClick()
                } finally {
                    this.doActivateItem(item)
                }
            })
        }
        return item;
    }

    ativateItemByPlace(place: Place) {
        const item = this.activableMenuItenMap.get(place)
        item && (this.doActivateItem(item))
    }

    private doActivateItem(itemToBeActivate: MenuItem) {
        for (const item of this.activableMenuItenMap.values()) {
            item.active = itemToBeActivate === item;
        }
        this.owner.scope.menu.version++
    }

}